import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useContext } from 'react';
import { AuthContext } from '../../../AuthContext/Authcontext';
import ReusableTable from '../../../Layout/ReusableTable';
import baseURL , {defaultManagerId}from '../../../Apiurs/Apiurls';
import Navbar from '../../../Shared/AdminNavbar/Navbar';
import { FaEye, FaSave, FaEdit, FaTrash, FaComment, FaPaperPlane } from 'react-icons/fa';
import { RiFileList2Line } from "react-icons/ri";

import './Leads.css';

function AdminLeads() { 
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState([]);
  const [message, setMessage] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState({});
  const [selectedAssociate, setSelectedAssociate] = useState({});
  const [selectedSalesManager, setSelectedSalesManager] = useState({});
  const [selectedSalesAssociate, setSelectedSalesAssociate] = useState({});
  const [selectedStatus, setSelectedStatus] = useState({});
  const [assignedData, setAssignedData] = useState({});
  const [managerChanged, setManagerChanged] = useState({});
  const [associateChanged, setAssociateChanged] = useState({});
  const [salesManagerChanged, setSalesManagerChanged] = useState({});
  const [salesAssociateChanged, setSalesAssociateChanged] = useState({});
  const [statusChanged, setStatusChanged] = useState({});
  const [savedManagerIds, setSavedManagerIds] = useState({});

  const navigate = useNavigate();
  const { authToken, userId, userRole } = useContext(AuthContext);

  // Fetch managers and their team members
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/employees/managers`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setManagers(response.data.data);
      } catch (error) {
        console.error('Error fetching managers:', error);
        setMessage('Failed to fetch managers');
      }
    };
    fetchManagers();
  }, [authToken]);

  // Fetch leads and assignments
  const fetchLeads = async () => {
    try {
      // Fetch leads
      const leadsResponse = await axios.get(`${baseURL}/api/contacts/new`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Fetch assignments
      const assignmentsResponse = await axios.get(`${baseURL}/api/lead-assignments`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (leadsResponse.status === 200) {
        const leads = leadsResponse.data;
        setData(leads);
        setFilteredData(leads);

        const managerSelections = {};
        const associateSelections = {};
        const salesManagerSelections = {};
        const salesAssociateSelections = {};
        const statusSelections = {};
        const assignments = assignmentsResponse.data;

        leads.forEach((lead) => {
          const assignment = assignments[lead.id] || {};
          managerSelections[lead.id] = assignment.manager_id?.toString() || '';
          associateSelections[lead.id] = assignment.associate_id?.toString() || '';
          salesManagerSelections[lead.id] = assignment.manager_1_id?.toString() || '';
          salesAssociateSelections[lead.id] = assignment.associate_1_id?.toString() || '';
          statusSelections[lead.id] = lead.status || 'New'; // Default to 'New' if no status
        });

        setSelectedManager(managerSelections);
        setSelectedAssociate(associateSelections);
        setSelectedSalesManager(salesManagerSelections);
        setSelectedSalesAssociate(salesAssociateSelections);
        setSelectedStatus(statusSelections);
        setAssignedData(assignments);
      }
    } catch (error) {
      console.error('Error fetching leads or assignments:', error);
      setMessage(error.response?.data?.message || 'Failed to fetch leads or assignments');
    }
  };

  // Fetch leads and assignments initially and every 2 seconds
  useEffect(() => {
    fetchLeads();
  }, [authToken, managers.length]);

  // Handle status update
  const handleStatusUpdate = async (leadId) => {
    try {
      const newStatus = selectedStatus[leadId];
      
      const response = await axios.put(
        `${baseURL}/api/contacts/${leadId}/status`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setMessage('Status updated successfully');
      setStatusChanged((prev) => ({ ...prev, [leadId]: false }));
      
      // Update the data with the new status
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, status: newStatus }
            : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, status: newStatus }
            : lead
        )
      );
      fetchLeads();
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Handle manager assignment
  const handleManagerAssignment = async (leadId) => {
    try {
      const selectedId = selectedManager[leadId];
      if (!selectedId) {
        setMessage('Please select a manager');
        return;
      }

      const isSelf = selectedId.toString() === userId.toString();

      const response = await axios.put(
        `${baseURL}/api/leadcrm/${leadId}/assign-user`,
        {
          assigned_by: userId,
          assigned_to: selectedId,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setMessage(
        isSelf ? 'Self-assignment saved successfully' : 'Manager assignment saved successfully'
      );
      setManagerChanged((prev) => ({ ...prev, [leadId]: false }));
      setSavedManagerIds((prev) => ({ ...prev, [leadId]: true }));
      setAssignedData((prev) => ({
        ...prev,
        [leadId]: response.data.data,
      }));
      // Update lead data to reflect new assignment
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: selectedId }
            : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: selectedId }
            : lead
        )
      );
      // Reset associate selection after manager assignment
      setSelectedAssociate((prev) => ({ ...prev, [leadId]: '' }));
      setAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
    } catch (error) {
      console.error('Error assigning manager:', error);
      setMessage(error.response?.data?.message || 'Failed to save manager assignment');
    }
  };

  // Handle associate assignment
  const handleAssociateAssignment = async (leadId) => {
    try {
      const assignedTo = selectedAssociate[leadId];
      if (!assignedTo) {
        setMessage('Please select an associate');
        return;
      }

      const response = await axios.put(
        `${baseURL}/api/leadcrm/${leadId}/assign-user`,
        {
          assigned_by: userId,
          assigned_to: assignedTo,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setMessage('Associate assignment saved successfully');
      setAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
      setAssignedData((prev) => ({
        ...prev,
        [leadId]: response.data.data,
      }));
      // Update lead data to reflect new assignment
      setData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: assignedTo }
            : lead
        )
      );
      setFilteredData((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, assigned_by: userId.toString(), assigned_to: assignedTo }
            : lead
        )
      );
    } catch (error) {
      console.error('Error assigning associate:', error);
      setMessage(error.response?.data?.message || 'Failed to save associate assignment');
    }
  };

  // Sales Manager Save Button
  // const handleSalesManagerAssignment = async (leadId) => {
  //   try {
  //     const selectedId = selectedSalesManager[leadId];
  //     if (!selectedId) {
  //       setMessage('Please select a Sales Manager');
  //       return;
  //     }

  //     const response = await axios.put(
  //       `${baseURL}/api/leadcrm/${leadId}/assign-user/user`,
  //       {
  //         assigned_by_1: userId,
  //         assigned_to_1: selectedId,
  //       },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${authToken}`,
  //         },
  //       }
  //     );

  //     setMessage('Sales Manager assignment saved successfully');
  //     setSalesManagerChanged((prev) => ({ ...prev, [leadId]: false }));
  //     setAssignedData((prev) => ({
  //       ...prev,
  //       [leadId]: { ...prev[leadId], ...response.data.data },
  //     }));
  //     // Update lead data to reflect new assignment
  //     setData((prev) =>
  //       prev.map((lead) =>
  //         lead.id === leadId
  //           ? { ...lead, assigned_by_1: userId.toString(), assigned_to_1: selectedId }
  //           : lead
  //       )
  //     );
  //     setFilteredData((prev) =>
  //       prev.map((lead) =>
  //         lead.id === leadId
  //           ? { ...lead, assigned_by_1: userId.toString(), assigned_to_1: selectedId }
  //           : lead
  //       )
  //     );
  //     // Reset sales associate selection after manager assignment
  //     setSelectedSalesAssociate((prev) => ({ ...prev, [leadId]: '' }));
  //     setSalesAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
  //   } catch (error) {
  //     console.error('Error assigning Sales Manager:', error);
  //     setMessage(error.response?.data?.message || 'Failed to save Sales Manager assignment');
  //   }
  // };


  // // Sales Associate Save Button
  // const handleSalesAssociateAssignment = async (leadId) => {
  //   try {
  //     const assignedTo = selectedSalesAssociate[leadId];
  //     if (!assignedTo) {
  //       setMessage('Please select a Sales Associate');
  //       return;
  //     }

  //     const response = await axios.put(
  //       `${baseURL}/api/leadcrm/${leadId}/assign-user/user`,
  //       {
  //         assigned_by_1: userId,
  //         assigned_to_1: assignedTo,
  //       },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${authToken}`,
  //         },
  //       }
  //     );

  //     setMessage('Sales Associate assignment saved successfully');
  //     setSalesAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
  //     setAssignedData((prev) => ({
  //       ...prev,
  //       [leadId]: { ...prev[leadId], ...response.data.data },
  //     }));
  //     // Update lead data to reflect new assignment
  //     setData((prev) =>
  //       prev.map((lead) =>
  //         lead.id === leadId
  //           ? { ...lead, assigned_by_1: userId.toString(), assigned_to_1: assignedTo }
  //           : lead
  //       )
  //     );
  //     setFilteredData((prev) =>
  //       prev.map((lead) =>
  //         lead.id === leadId
  //           ? { ...lead, assigned_by_1: userId.toString(), assigned_to_1: assignedTo }
  //           : lead
  //       )
  //     );
  //   } catch (error) {
  //     console.error('Error assigning Sales Associate:', error);
  //     setMessage(error.response?.data?.message || 'Failed to save Sales Associate assignment');
  //   }
  // };
  // Sales Associate Save Button
const handleSalesAssociateAssignment = async (leadId) => {
  try {
    const assignedTo = selectedSalesAssociate[leadId];
    if (!assignedTo) {
      setMessage("Please select a Sales Associate");
      return;
    }

    const response = await axios.put(
      `${baseURL}/api/leadcrm/${leadId}/assign-user/user`,
      {
        assigned_by_1: userId,
        assigned_to_1: assignedTo,
        manager_1_id: defaultManagerId, // ✅ using global variable
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    setMessage("Sales Associate assignment saved successfully");
    setSalesAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
    setAssignedData((prev) => ({
      ...prev,
      [leadId]: { ...prev[leadId], ...response.data.data },
    }));

    setData((prev) =>
      prev.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              assigned_by_1: userId.toString(),
              assigned_to_1: assignedTo,
              manager_1_id: defaultManagerId.toString(),
            }
          : lead
      )
    );
    setFilteredData((prev) =>
      prev.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              assigned_by_1: userId.toString(),
              assigned_to_1: assignedTo,
              manager_1_id: defaultManagerId.toString(),
            }
          : lead
      )
    );
  } catch (error) {
    console.error("Error assigning Sales Associate:", error);
    setMessage(
      error.response?.data?.message ||
        "Failed to save Sales Associate assignment"
    );
  }
};


  // Handle edit action
  const handleEdit = (row) => {
    navigate(`/lead_view/${row.id}`);
  };

  // Handle delete action
  const handleDelete = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await axios.delete(`${baseURL}/api/contacts/${leadId}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setMessage('Lead deleted successfully');
        fetchLeads();
      } catch (error) {
        console.error('Error deleting lead:', error);
        setMessage(error.response?.data?.message || 'Failed to delete lead');
      }
    }
  };

  // Handle add user
  const handleAddUser = () => {
    navigate('/a-add-lead');
  };

  // Handle send quotation - navigate to quotation form
  const handleSendQuotation = (lead) => {
    navigate('/send-budget', { state: { lead } });
  };

  // Reusable truncate cell
  const TruncateCell = ({ value, maxLength = 20 }) => {
    if (value === null || value === undefined) {
      return <span>-</span>;
    }
    const str = String(value);
    return (
      <div
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: `${maxLength * 8}px`,
        }}
        title={str}
      >
        {str.length > maxLength ? str.slice(0, maxLength) + '...' : str}
      </div>
    );
  };

  // Table columns
 const columns = [
  {
    key: 'leadId',
    title: 'LeadID',
    render: (row) => <TruncateCell value={`Lead00${row.id}`} maxLength={10} />,
  },
  {
    key: 'lead_name',
    title: 'Lead Name',
    render: (row) => <TruncateCell value={row.lead_name} maxLength={15} />,
  },
  {
    key: 'business_name',
    title: 'Business Name',
    render: (row) => <TruncateCell value={row.business_name} maxLength={15} />,
  },
  {
    key: 'lead_source',
    title: 'Source',
    render: (row) => <TruncateCell value={row.lead_source} maxLength={12} />,
  },
  {
    key: 'contact_number',
    title: 'Contact Number',
    render: (row) => <TruncateCell value={row.contact_number} maxLength={12} />,
  },
  {
    key: 'email',
    title: 'Email',
    render: (row) => <TruncateCell value={row.email} maxLength={20} />,
  },
  {
    key: 'preferred_contact_method',
    title: 'Contact Method',
    render: (row) => <TruncateCell value={row.preferred_contact_method} maxLength={15} />,
  },
  {
    key: 'status',
    title: 'Status',
    render: (row) => {
      const leadId = row.id;
      return (
        <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
          <select
            className="form-select form-select-sm"
            value={selectedStatus[leadId] || 'New'}
            onChange={(e) => {
              const newStatus = e.target.value;
              setSelectedStatus((prev) => ({ ...prev, [leadId]: newStatus }));
              setStatusChanged((prev) => ({ ...prev, [leadId]: true }));
            }}
          >
            <option value="New">New</option>
            <option value="Qualified">Qualified</option>
            <option value="Not Qualified">Not Qualified</option>
            <option value="Loss">Loss</option>
          </select>
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleStatusUpdate(leadId)}
            disabled={!statusChanged[leadId]}
            title="Save Status"
          >
            <FaSave />
          </button>
        </div>
      );
    },
  },
  {
    key: 'manager',
    title: 'Quotation Manager',
    render: (row) => {
      const leadId = row.id;
      const currentAssignment = assignedData[leadId] || {};
      const isSelfAssigned = currentAssignment.manager_id === userId.toString();

      // Filter managers with role "manager"
      const quotationManagers = managers.filter(manager => manager.role === 'manager');

      return (
        <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
          <select
            className="form-select form-select-sm"
            value={selectedManager[leadId] || currentAssignment.manager_id || ''}
            onChange={(e) => {
              const newManagerId = e.target.value;
              setSelectedManager((prev) => ({ ...prev, [leadId]: newManagerId }));
              setManagerChanged((prev) => ({ ...prev, [leadId]: true }));
              setSelectedAssociate((prev) => ({ ...prev, [leadId]: '' }));
              setAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
              setSavedManagerIds((prev) => ({ ...prev, [leadId]: false }));
            }}
          >
            <option value="">Select Manager</option>
            <option
              value={userId}
              style={{
                fontWeight: isSelfAssigned ? 'bold' : 'normal',
                backgroundColor: isSelfAssigned ? '#e6f7ff' : 'transparent',
              }}
            >
              Self {isSelfAssigned ? ' (Assigned)' : ''}
            </option>
            {quotationManagers.map((manager) => {
              const isSelected = currentAssignment.manager_id === manager.id.toString();
              return (
                <option
                  key={manager.id}
                  value={manager.id}
                  style={{
                    fontWeight: isSelected ? 'bold' : 'normal',
                    backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                  }}
                >
                  {manager.name}
                  {isSelected ? ' (Assigned)' : ''}
                </option>
              );
            })}
          </select>
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleManagerAssignment(leadId)}
            disabled={!managerChanged[leadId]}
            title="Save Manager"
          >
            <FaSave />
          </button>
        </div>
      );
    },
  },
  {
    key: 'associate',
    title: 'Quotation Associate',
    render: (row) => {
      const leadId = row.id;
      const currentAssignment = assignedData[leadId] || {};
      const managerId = selectedManager[leadId] || currentAssignment.manager_id || '';
      const manager = managers.find((m) => m.id.toString() === managerId);
      // Filter team members with role "employee"
      const quotationAssociates = manager?.teamMembers?.filter(member => member.role === 'employee') || [];

      return (
        <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
          <select
            className="form-select form-select-sm"
            value={selectedAssociate[leadId] || currentAssignment.associate_id || ''}
            onChange={(e) => {
              const newAssociateId = e.target.value;
              setSelectedAssociate((prev) => ({ ...prev, [leadId]: newAssociateId }));
              setAssociateChanged((prev) => ({ ...prev, [leadId]: true }));
            }}
            disabled={!managerId}
          >
            <option value="">Select Associate</option>
            {quotationAssociates.map((member) => {
              const isSelected = currentAssignment.associate_id === member.id.toString();
              return (
                <option
                  key={member.id}
                  value={member.id}
                  style={{
                    fontWeight: isSelected ? 'bold' : 'normal',
                    backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                  }}
                >
                  {member.name}
                  {isSelected ? ' (Assigned)' : ''}
                </option>
              );
            })}
          </select>
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleAssociateAssignment(leadId)}
            disabled={!associateChanged[leadId] || !managerId}
            title="Save Associate"
          >
            <FaSave />
          </button>
        </div>
      );
    },
  },
  // {
  //   key: 'salesManager',
  //   title: 'Sales Manager',
  //   render: (row) => {
  //     const leadId = row.id;
  //     const currentAssignment = assignedData[leadId] || {};
  //     const isSelfAssigned = currentAssignment.manager_1_id === userId.toString();

  //     // Filter managers with role "s-manager"
  //     const salesManagers = managers.filter(manager => manager.role === 's-manager');

  //     return (
  //       <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
  //         <select
  //           className="form-select form-select-sm"
  //           value={selectedSalesManager[leadId] || currentAssignment.manager_1_id?.toString() || ''}
  //           onChange={(e) => {
  //             const newManagerId = e.target.value;
  //             setSelectedSalesManager((prev) => ({ ...prev, [leadId]: newManagerId }));
  //             setSalesManagerChanged((prev) => ({ ...prev, [leadId]: true }));
  //             setSelectedSalesAssociate((prev) => ({ ...prev, [leadId]: '' }));
  //             setSalesAssociateChanged((prev) => ({ ...prev, [leadId]: false }));
  //           }}
  //         >
  //           <option value="">Select Sales Manager</option>
  //           <option
  //             value={userId}
  //             style={{
  //               fontWeight: isSelfAssigned ? 'bold' : 'normal',
  //               backgroundColor: isSelfAssigned ? '#e6f7ff' : 'transparent',
  //             }}
  //           >
  //             Self {isSelfAssigned ? ' (Assigned)' : ''}
  //           </option>
  //           {salesManagers.map((manager) => {
  //             const isSelected = currentAssignment.manager_1_id?.toString() === manager.id.toString();
  //             return (
  //               <option
  //                 key={manager.id}
  //                 value={manager.id}
  //                 style={{
  //                   fontWeight: isSelected ? 'bold' : 'normal',
  //                   backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
  //                 }}
  //               >
  //                 {manager.name}
  //                 {isSelected ? ' (Assigned)' : ''}
  //               </option>
  //             );
  //           })}
  //         </select>
  //         <button
  //           className="btn btn-sm btn-success"
  //           onClick={() => handleSalesManagerAssignment(leadId)}
  //           disabled={!salesManagerChanged[leadId]}
  //           title="Save Sales Manager"
  //         >
  //           <FaSave />
  //         </button>
  //       </div>
  //     );
  //   },
  // },

  // {
  //   key: 'salesAssociate',
  //   title: 'Sales Associate',
  //   render: (row) => {
  //     const leadId = row.id;
  //     const currentAssignment = assignedData[leadId] || {};
  //     const managerId = selectedSalesManager[leadId] || currentAssignment.manager_1_id?.toString() || '';
  //     const manager = managers.find((m) => m.id.toString() === managerId);
  //     // Filter team members with role "s-employee"
  //     const salesAssociates = manager?.teamMembers?.filter(member => member.role === 's-employee') || [];

  //     return (
  //       <div className="d-flex align-items-center gap-2" style={{ minWidth: '190px' }}>
  //         <select
  //           className="form-select form-select-sm"
  //           value={selectedSalesAssociate[leadId] || currentAssignment.associate_1_id?.toString() || ''}
  //           onChange={(e) => {
  //             const newAssociateId = e.target.value;
  //             setSelectedSalesAssociate((prev) => ({ ...prev, [leadId]: newAssociateId }));
  //             setSalesAssociateChanged((prev) => ({ ...prev, [leadId]: true }));
  //           }}
  //           disabled={!managerId}
  //         >
  //           <option value="">Select Sales Associate</option>
  //           {salesAssociates.map((member) => {
  //             const isSelected = currentAssignment.associate_1_id?.toString() === member.id.toString();
  //             return (
  //               <option
  //                 key={member.id}
  //                 value={member.id}
  //                 style={{
  //                   fontWeight: isSelected ? 'bold' : 'normal',
  //                   backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
  //                 }}
  //               >
  //                 {member.name}
  //                 {isSelected ? ' (Assigned)' : ''}
  //               </option>
  //             );
  //           })}
  //         </select>
  //         <button
  //           className="btn btn-sm btn-success"
  //           onClick={() => handleSalesAssociateAssignment(leadId)}
  //           disabled={!salesAssociateChanged[leadId] || !managerId}
  //           title="Save Sales Associate"
  //         >
  //           <FaSave />
  //         </button>
  //       </div>
  //     );
  //   },
  // },
{
  key: "salesAssociate",
  title: "Sales Associate",
  render: (row) => {
    const leadId = row.id;
    const currentAssignment = assignedData[leadId] || {};

    // 🔹 Use imported defaultManagerId
 const defaultSalesManager = managers.find(
  (m) => Number(m.id) === Number(defaultManagerId)
);


    const salesAssociates =
      defaultSalesManager?.teamMembers?.filter(
        (member) => member.role === "s-employee"
      ) || [];

    return (
      <div
        className="d-flex align-items-center gap-2"
        style={{ minWidth: "190px" }}
      >
        <select
          className="form-select form-select-sm"
          value={
            selectedSalesAssociate[leadId] ||
            currentAssignment.associate_1_id?.toString() ||
            ""
          }
          onChange={(e) => {
            const newAssociateId = e.target.value;
            setSelectedSalesAssociate((prev) => ({
              ...prev,
              [leadId]: newAssociateId,
            }));
            setSalesAssociateChanged((prev) => ({
              ...prev,
              [leadId]: true,
            }));
          }}
        >
          <option value="">Select Sales Associate</option>
          {salesAssociates.map((member) => {
            const isSelected =
              currentAssignment.associate_1_id?.toString() ===
              member.id.toString();
            return (
              <option
                key={member.id}
                value={member.id}
                style={{
                  fontWeight: isSelected ? "bold" : "normal",
                  backgroundColor: isSelected ? "#e6f7ff" : "transparent",
                }}
              >
                {member.name}
                {isSelected ? " (Assigned)" : ""}
              </option>
            );
          })}
        </select>

        <button
          className="btn btn-sm btn-success"
          onClick={() => handleSalesAssociateAssignment(leadId)}
          disabled={!salesAssociateChanged[leadId]}
          title="Save Sales Associate"
        >
          <FaSave />
        </button>
      </div>
    );
  },
},

  {
    key: 'actions',
    title: 'Actions',
    render: (row) => (
      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() =>
            navigate(`/a-newviewlead/${row.id}`, { state: { lead: row } })
          }
          title="View"
        >
          <RiFileList2Line  />
        </button>

        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => handleEdit(row)}
          title="Edit"
        >
               <FaEye  />

             {/* <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      navigate(`/opportunity_view/${row.id}`, { state: { lead: row } })
                    }
                    title="View-Edit"
                  >
                    <FaEye  />
                  </button> */}
        </button>
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => handleDelete(row.id)}
          title="Delete"
        >
          <FaTrash />
        </button>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => navigate(`/a-comments/${row.id}`, { state: { leadid: row.id } })}
          title="Comment"
        >
          <FaComment />
        </button>
        <button
          className="btn btn-sm btn-outline-info"
          onClick={() => handleSendQuotation(row)}
          title="Send Budget Quotation"
        >
          <FaPaperPlane />
        </button>
      </div>
    ),
  },
];

  return (
    <div className="AdminLeadscontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`AdminLeads ${collapsed ? 'collapsed' : ''}`}>
        <div className="AdminLeads-container mb-5">
          <div className="AdminLeads-table-container">
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn-success" onClick={handleAddUser}>
                + Add Lead
              </button>
            </div>
            {message && (
              <div className={`alert ${message.includes('successfully') ? 'alert-success' : 'alert-danger'}`}>
                {message}
              </div>
            )}
            <ReusableTable
              title="Admin Leads"
              data={filteredData}
              columns={columns}
              initialEntriesPerPage={5}
              searchPlaceholder="Search leads..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLeads;